import React, { useState } from 'react';
import { RelativeTime } from '@/components/RelativeTime';
import {
  AlertCircle,
  Inbox,
  Mail,
  MoreHorizontal,
  Search,
  Send,
  Trash2,
  UserPlus,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import {
  type Invitation,
  useCreateInvitation,
  useDeleteInvitation,
  useInvitations,
  useResendInvitation,
} from '../../hooks/useInvitations';
import { type OrgUser, useUsers } from '../../hooks/useUsers';
import { useTeams, formatTeamName } from '../../hooks/useTeams';
import { useContexts } from '../../hooks/useContexts';
import { useResourceLimits } from '../../hooks/useResourceLimits';
import ConfirmationModal from '../../components/ConfirmationModal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const NO_CONTEXT = '__none__';
const ALL_TEAMS = '__all__';

function InvitationStatus({ invitation }: { invitation: Invitation }) {
  if (invitation.acceptedAt) {
    return (
      <Badge className="bg-sev-4/15 text-sev-4 border-0 font-normal">Accepted</Badge>
    );
  }
  if (invitation.isExpired) {
    return (
      <Badge className="bg-sev-1/15 text-sev-1 border-0 font-normal">Expired</Badge>
    );
  }
  if (invitation.isValid) {
    return (
      <Badge className="bg-sev-5/15 text-sev-5 border-0 font-normal">Pending</Badge>
    );
  }
  return (
    <Badge className="bg-sev-3/15 text-foreground border-0 font-normal">Invalid</Badge>
  );
}

export default function Users() {
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const [isInviteModalActive, setIsInviteModalActive] = useState(false);
  const [isDeleteConfirmActive, setIsDeleteConfirmActive] = useState(false);
  const [invitationToDelete, setInvitationToDelete] = useState<Invitation | null>(null);

  const [memberSearch, setMemberSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState(ALL_TEAMS);

  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedContextId, setSelectedContextId] = useState(NO_CONTEXT);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    data: orgUsers,
    isLoading: isUsersLoading,
    isError: isUsersError,
    error: usersError,
  } = useUsers(organizationId);
  const {
    data: invitations,
    isLoading: isInvitationsLoading,
    isError: isInvitationsError,
    error: invitationsError,
  } = useInvitations(organizationId);
  const { data: teams } = useTeams(organizationId);
  const { data: contexts } = useContexts(organizationId);
  const createInvitationMutation = useCreateInvitation();
  const deleteInvitationMutation = useDeleteInvitation();
  const resendInvitationMutation = useResendInvitation();
  const { data: resourceLimits } = useResourceLimits();
  const userLimits = resourceLimits?.users;

  const resetInviteForm = () => {
    setInviteEmail('');
    setSelectedTeamId('');
    setSelectedContextId(NO_CONTEXT);
    setSubmitError(null);
  };

  const handleInviteSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!inviteEmail.trim()) { setSubmitError('Email is required'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setSubmitError('Please enter a valid email address');
      return;
    }
    if (!selectedTeamId) { setSubmitError('Please select a team'); return; }

    try {
      await createInvitationMutation.mutateAsync({
        email: inviteEmail,
        teamId: selectedTeamId,
        contextId: selectedContextId === NO_CONTEXT ? undefined : selectedContextId,
      });
      setIsInviteModalActive(false);
      resetInviteForm();
    } catch (err) {
      const axiosError = err as {
        response?: { data?: { error?: string; description?: string } };
      };
      setSubmitError(
        axiosError.response?.data?.description ||
          axiosError.response?.data?.error ||
          'Failed to send invitation',
      );
    }
  };

  const handleDeleteClick = (invitation: Invitation) => {
    setInvitationToDelete(invitation);
    setIsDeleteConfirmActive(true);
  };

  const handleDeleteConfirm = async () => {
    if (!invitationToDelete) return;
    try {
      await deleteInvitationMutation.mutateAsync(invitationToDelete.id);
      setIsDeleteConfirmActive(false);
      setInvitationToDelete(null);
    } catch (err) {
      console.error('Failed to delete invitation:', err);
    }
  };

  const handleResendClick = async (invitation: Invitation) => {
    try {
      await resendInvitationMutation.mutateAsync({ id: invitation.id, request: {} });
    } catch (err) {
      console.error('Failed to resend invitation:', err);
    }
  };

  if (isUsersError || isInvitationsError) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {isUsersError
              ? `Error loading users: ${usersError.message || 'Unknown error'}`
              : `Error loading invitations: ${invitationsError?.message || 'Unknown error'}`}
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const memberQuery = memberSearch.trim().toLowerCase();
  const isRosterFiltered = memberQuery !== '' || teamFilter !== ALL_TEAMS;
  const filteredMembers = (orgUsers ?? []).filter((member) => {
    if (teamFilter !== ALL_TEAMS && !member.teams.some((t) => t.id === teamFilter)) {
      return false;
    }
    if (!memberQuery) return true;
    return (
      `${member.firstName} ${member.lastName}`.toLowerCase().includes(memberQuery) ||
      member.email.toLowerCase().includes(memberQuery)
    );
  });

  const memberColumns: readonly ResponsiveTableColumn<OrgUser>[] = [
    {
      key: 'name',
      label: 'Name',
      hideMobileLabel: true,
      render: (member) => (
        <div className="flex items-center gap-1.5">
          <span className="font-medium">
            {`${member.firstName} ${member.lastName}`.trim() || member.email}
          </span>
          {!member.emailVerifiedAt ? (
            <Badge className="bg-sev-3/15 text-foreground border-0 font-normal">
              Unverified
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      render: (member) => (
        <span className="text-xs text-muted-foreground">{member.email}</span>
      ),
    },
    {
      key: 'teams',
      label: 'Teams',
      render: (member) =>
        member.teams.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {member.teams.map((team) => (
              <Badge key={team.id} variant="secondary" className="font-normal">
                {formatTeamName(team)}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">No teams</span>
        ),
    },
    {
      key: 'role',
      label: 'Role',
      // Owner outranks admin and is shown instead of it: ownership already
      // carries every administrative permission, so listing both would read as
      // two grants where there is one.
      render: (member) =>
        member.isOwner ? (
          <Badge className="bg-sev-4/15 text-sev-4 border-0 font-normal">Owner</Badge>
        ) : member.isAdmin ? (
          <Badge className="bg-sev-5/15 text-sev-5 border-0 font-normal">Admin</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">Member</span>
        ),
    },
    {
      key: 'joined',
      label: 'Joined',
      hideAt: 'mobile',
      render: (member) => (
        <span className="text-xs text-muted-foreground">
          <RelativeTime value={member.createdAt} />
        </span>
      ),
    },
  ];

  const inviteColumns: readonly ResponsiveTableColumn<Invitation>[] = [
    {
      key: 'email',
      label: 'Email',
      hideMobileLabel: true,
      render: (inv) => <span className="font-medium">{inv.email}</span>,
    },
    {
      key: 'invitedBy',
      label: 'Invited by',
      render: (inv) => (
        <span className="text-xs text-muted-foreground">{inv.invitedByName || 'Unknown'}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (inv) => <InvitationStatus invitation={inv} />,
    },
    {
      key: 'expires',
      label: 'Expires',
      render: (inv) =>
        inv.isExpired ? (
          <span className="text-xs text-sev-1">
            Expired <RelativeTime value={inv.expiresAt} />
          </span>
        ) : (
          <span className="text-xs">
            <RelativeTime value={inv.expiresAt} />
          </span>
        ),
    },
    {
      key: 'created',
      label: 'Created',
      hideAt: 'mobile',
      render: (inv) => (
        <span className="text-xs text-muted-foreground">
          <RelativeTime value={inv.createdAt} />
        </span>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="User management"
        subtitle="Invite and manage users in your organization"
        primary={
          <Button onClick={() => { setIsInviteModalActive(true); }} className="gap-1.5">
            <UserPlus className="w-4 h-4" />
            Invite user
          </Button>
        }
      />

      <h3 className="text-sm font-semibold mb-2">
        Members
        {orgUsers ? (
          <span className="ml-1.5 font-normal text-muted-foreground">
            {isRosterFiltered ? (
              <>
                {filteredMembers.length} of {orgUsers.length}
              </>
            ) : (
              orgUsers.length
            )}{' '}
            {orgUsers.length === 1 ? 'user' : 'users'}
          </span>
        ) : null}
      </h3>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
        <div className="relative w-full sm:max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            placeholder="Search by name or email…"
            value={memberSearch}
            onChange={(e) => { setMemberSearch(e.target.value); }}
            className="pl-9"
            aria-label="Search members"
          />
        </div>
        <Select value={teamFilter} onValueChange={setTeamFilter}>
          <SelectTrigger className="w-full sm:w-64" aria-label="Filter by team">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TEAMS}>All teams</SelectItem>
            {teams?.map((team) => (
              <SelectItem key={team.id} value={team.id}>
                {formatTeamName(team)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ResponsiveTable<OrgUser>
        columns={memberColumns}
        rows={filteredMembers}
        rowKey={(member) => member.id}
        isLoading={isUsersLoading}
        empty={
          isRosterFiltered ? (
            <EmptyState
              variant="no-results"
              title="No members match"
              description="Try a different search or team filter."
              size="inline"
            />
          ) : (
            <EmptyState
              variant="no-data"
              icon={UsersIcon}
              title="No members yet"
              description='Click "Invite user" to add people to your organization.'
              size="inline"
            />
          )
        }
      />

      <h3 className="text-sm font-semibold mb-2 mt-8">Pending invitations</h3>

      <ResponsiveTable<Invitation>
        columns={inviteColumns}
        rows={invitations ?? []}
        rowKey={(inv) => inv.id}
        isLoading={isInvitationsLoading}
        rowActions={(invitation) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${invitation.email}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!invitation.acceptedAt ? (
                <DropdownMenuItem
                  onClick={() => { void handleResendClick(invitation); }}
                  disabled={resendInvitationMutation.isPending}
                  className="gap-2"
                >
                  <Send className="w-4 h-4" />
                  Resend
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                onClick={() => { handleDeleteClick(invitation); }}
                disabled={deleteInvitationMutation.isPending}
                className="gap-2 text-sev-1 focus:text-sev-1"
              >
                <Trash2 className="w-4 h-4" />
                Revoke
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        empty={
          <EmptyState
            variant="no-data"
            icon={Inbox}
            title="No pending invitations"
            description='Click "Invite user" to send an invitation.'
            size="inline"
          />
        }
      />

      <AppSheet
        open={isInviteModalActive}
        onOpenChange={(open) => {
          if (!open) {
            setIsInviteModalActive(false);
            resetInviteForm();
          }
        }}
        title="Invite new user"
        size="md"
      >
        <AppSheet.Body>
          {submitError ? (
            <Alert variant="destructive" className="mb-4 relative">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{submitError}</AlertDescription>
              <button
                type="button"
                onClick={() => { setSubmitError(null); }}
                className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </Alert>
          ) : null}

          <form
            id="invite-user-form"
            onSubmit={(e) => { void handleInviteSubmit(e); }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="iu-email">Email address</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  id="iu-email"
                  type="email"
                  placeholder="user@example.com"
                  value={inviteEmail}
                  onChange={(e) => { setInviteEmail(e.target.value); }}
                  required
                  autoFocus
                  className="pl-9"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                An invitation email will be sent with a link to join your organization.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="iu-team">Team *</Label>
              <Select value={selectedTeamId} onValueChange={setSelectedTeamId} required>
                <SelectTrigger id="iu-team">
                  <SelectValue placeholder="Select a team…" />
                </SelectTrigger>
                <SelectContent>
                  {teams?.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {formatTeamName(team)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The user will be added to this team and inherit its permissions.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="iu-context">Default context (optional)</Label>
              <Select value={selectedContextId} onValueChange={setSelectedContextId}>
                <SelectTrigger id="iu-context">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CONTEXT}>None (user can choose)</SelectItem>
                  {contexts?.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The context the user will land in after signing in for the first time.
              </p>
            </div>

            {userLimits ? (
              <Alert
                className={cn(
                  userLimits.atLimit
                    ? 'border-sev-1/40 bg-sev-1/10'
                    : userLimits.approachingLimit
                      ? 'border-sev-3/40 bg-sev-3/10'
                      : 'border-sev-5/30 bg-sev-5/5',
                )}
              >
                <AlertDescription>
                  <strong>Users:</strong> {userLimits.currentCount} of {userLimits.softLimit} used
                  {userLimits.atLimit
                    ? '. You have reached your user limit. Please upgrade your plan or contact support.'
                    : null}
                </AlertDescription>
              </Alert>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => {
              setIsInviteModalActive(false);
              resetInviteForm();
            }}
            disabled={createInvitationMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="invite-user-form"
            disabled={createInvitationMutation.isPending || (userLimits?.atLimit ?? false)}
          >
            {createInvitationMutation.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <ConfirmationModal
        isActive={isDeleteConfirmActive}
        title="Revoke invitation"
        message={`Are you sure you want to revoke the invitation for ${invitationToDelete?.email ?? ''}? This action cannot be undone.`}
        confirmText="Revoke invitation"
        isDanger={true}
        onConfirm={() => { void handleDeleteConfirm(); }}
        onCancel={() => {
          setIsDeleteConfirmActive(false);
          setInvitationToDelete(null);
        }}
        isLoading={deleteInvitationMutation.isPending}
      />
    </PageContainer>
  );
}
